import { type Workspace } from '@/features/iam/workspaces/Workspace'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { action, computed } from 'mobx'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { skip, switchMap } from 'rxjs'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { withAbortSignal } from '@/utils/rx-util'
import { type ISchemaBoundValidationContext } from 'validx'

/**
 * Filter of the {@link WorkspaceSelectorViewModel}.
 */
export interface WorkspaceSelectorFilter {
  searchText: string
}

/**
 * A form that targets a workspace picked with a
 * {@link WorkspaceSelectorViewModel}.
 */
export interface WorkspaceSelection {
  workspace?: Workspace
  workspaceSelectorVm: WorkspaceSelectorViewModel
  workspaceValidation: ISchemaBoundValidationContext<{ workspace?: Workspace }>
  setWorkspace: (workspace?: Workspace) => void
}

/**
 * Searches the workspaces whose service items and bundles the user manages.
 */
export class WorkspaceSelectorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _searchText = new SearchTextViewModel(500)

  /**
   * The scroll provider for workspaces.
   */
  readonly scrollProvider = new InfiniteScrollProvider<
    Workspace,
    WorkspaceSelectorFilter
  >({
    limit: 25,
    fetchItems: ({ filter }) =>
      Promise.resolve(this._fetchWorkspaces(filter?.searchText ?? '')),
  })

  constructor(
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
  ) {
    this._lifecycle.manage(
      this._searchText.debouncedValue$.pipe(
        // The current search text is replayed on subscribe; the selector only
        // refetches once the user types.
        skip(1),
        switchMap((searchText) =>
          withAbortSignal((signal) =>
            this.scrollProvider.fetch({ filter: { searchText }, signal }),
          ),
        ),
      ),
    )
  }

  @computed
  get searchText() {
    return this._searchText.value
  }

  /**
   * Filtered workspaces.
   */
  @computed
  get filteredWorkspaces() {
    return this.scrollProvider.items
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * Sets the search text.
   *
   * @param text
   */
  @action.bound
  setSearchText(text: string) {
    this._searchText.change(text)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private _fetchWorkspaces(searchText: string) {
    const text = searchText.toLowerCase()
    const workspaces = this._getWorkspaceMemberships()
      .filter(
        (wms) =>
          wms.member.permissions.manageWorkspaceInventoryItems &&
          wms.workspace.name.toLowerCase().includes(text),
      )
      .map((wms) => wms.workspace)
    return { data: workspaces, total: workspaces.length, next: null }
  }
}
